import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { X, Pencil, Check, Copy } from 'lucide-react';

// DeviceIcon import
import DeviceIcon from '../../../deviceIcons';

// Import Dynamic Trait Renderer
import { DynamicTrait, DynamicAttribute, getTraitStyleId, getAttributeStyleId } from '../../DynamicTraitRenderer';
import { TraitStyleConfig, AttributeStyleConfig, IconStyleId, DeviceModalConfig } from '../../types';

export interface BlindDevice {
  deviceId: string;
  baseSN?: string;
  deviceName?: string;
  nickname?: string;
  location?: string;
  groupName?: string;
  isOnline?: boolean;
  openPercent?: number;
  traits?: string[];
  attributes?: Record<string, unknown>;
  system?: {
    endpointRelationship?: {
      classification?: string;
    };
  };
}

export interface BlindModalProps {
  device: BlindDevice;
  onClose: () => void;
  onTraitControl: (
    device: BlindDevice,
    trait: string,
    action: string,
    params?: Record<string, unknown>
  ) => Promise<void>;
  onSaveNickname?: (device: BlindDevice, nickname: string | null) => Promise<void>;
  config?: Partial<DeviceModalConfig>;
  isMergedDevice?: boolean;
  onToggleMerge?: (baseSN: string) => void;
}

/**
 * Blind Modal Template
 * 
 * 捲簾控制 Modal
 * - OpenClose trait: 開啟百分比控制 (0-100%)
 * 
 * MCP Command: OpenClose
 * Parameters: { openPercent: 0-100 }
 */
export const Template01_Modern: React.FC<BlindModalProps> = ({
  device,
  onClose,
  onTraitControl,
  onSaveNickname,
  config,
  isMergedDevice = false,
  onToggleMerge,
}) => {
  // Nickname editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(device.nickname || device.deviceName || '');
  const blockSyncUntilRef = useRef<number>(0);
  const isSavingRef = useRef(false);

  // Extract styles from config
  const traitStyles: TraitStyleConfig = config?.traitStyles || {};
  const attributeStyles: AttributeStyleConfig = config?.attributeStyles || {};
  const smallIconStyle: IconStyleId = (config?.smallIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const bigIconStyle: IconStyleId = (config?.bigIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const hiddenComponents: string[] = config?.hiddenComponents || [];

  // Sync editedName when device prop changes, but skip if we just saved
  useEffect(() => {
    if (!isEditingName && !isSavingRef.current && Date.now() > blockSyncUntilRef.current) {
      setEditedName(device.nickname || device.deviceName || '');
    }
  }, [device.nickname, device.deviceName, isEditingName]);

  // Device state
  const openPercent = device.openPercent ?? 0;
  const isOpen = openPercent > 0;
  const displayName = device.nickname || device.deviceName || device.deviceId;
  const displaySN = isMergedDevice ? device.baseSN : device.deviceId;

  // Multi-channel check
  const isMultiChannel = device.system?.endpointRelationship?.classification === 'multi_channel';

  // Handle position change
  const handlePositionChange = useCallback((value: number) => {
    onTraitControl(device, 'OpenClose', 'set', { openPercent: value });
  }, [device, onTraitControl]);

  // Handle nickname save
  const handleSaveNickname = useCallback(async () => {
    if (onSaveNickname && editedName !== (device.nickname || device.deviceName)) {
      isSavingRef.current = true;
      blockSyncUntilRef.current = Date.now() + 3000;
      await onSaveNickname(device, editedName || null);
      isSavingRef.current = false;
    }
    setIsEditingName(false);
  }, [device, editedName, onSaveNickname]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveNickname();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
      setEditedName(device.nickname || device.deviceName || '');
    }
  }, [handleSaveNickname, device.nickname, device.deviceName]);

  // Copy SN to clipboard
  const handleCopySN = useCallback(() => {
    navigator.clipboard.writeText(displaySN || '');
  }, [displaySN]);

  // Handle backdrop click - close modal or cancel editing
  const handleBackdropClick = useCallback(() => {
    if (isEditingName) {
      setIsEditingName(false);
      setEditedName(device.nickname || device.deviceName || '');
    } else {
      onClose();
    }
  }, [isEditingName, device.nickname, device.deviceName, onClose]);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4" 
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-gradient-to-b from-[#0a1628] via-[#0d1d35] to-[#0a1628] rounded-3xl w-full max-w-sm max-h-[90vh] overflow-hidden shadow-2xl border border-slate-800/50" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <DeviceIcon 
              deviceType="blind" 
              isOn={isOpen}
              size={20}
              styleId={smallIconStyle}
            />
            {isEditingName ? (
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                className="bg-slate-800 text-white px-2 py-1 rounded text-sm flex-1 min-w-0"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span className="text-white font-medium text-sm truncate">{displayName}</span>
            )}
            {isEditingName ? (
              <button onClick={handleSaveNickname} className="text-cyan-400 hover:text-cyan-300">
                <Check className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={() => setIsEditingName(true)} className="text-slate-400 hover:text-white">
                <Pencil className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <DynamicAttribute
              attributeName="StatusIndicator"
              styleId={getAttributeStyleId('StatusIndicator', attributeStyles)}
              componentProps={{
                online: device.isOnline,
                label: device.isOnline ? 'ONLINE' : 'OFFLINE'
              }}
            />
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 pb-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Big Icon - Visual representation using DeviceIcon */}
          {!hiddenComponents.includes('BigIcon') && (
            <div className="flex justify-center py-6">
              <div className={`w-32 h-32 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                isOpen
                  ? 'bg-gradient-to-br from-cyan-500/30 to-blue-500/30 border-2 border-cyan-400/50 shadow-lg shadow-cyan-500/30'
                  : 'bg-slate-800/50 border-2 border-slate-700'
              }`}>
                <DeviceIcon
                  deviceType="blind"
                  isOn={isOpen}
                  size={64}
                  styleId={bigIconStyle}
                  className={isOpen ? 'text-cyan-400' : 'text-slate-400'}
                />
              </div>
            </div>
          )}

          {/* Position control */}
          {!hiddenComponents.includes('OpenClose') && (
            <DynamicTrait
              traitName="OpenClose"
              styleId={getTraitStyleId('OpenClose', traitStyles)}
              componentProps={{
                value: openPercent,
                onChange: handlePositionChange,
                disabled: !device.isOnline,
                label: "捲簾位置",
                colorScheme: "cyan"
              }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-800/50 space-y-3">
          {/* Multi-channel toggle */}
          {isMultiChannel && onToggleMerge && device.baseSN && (
            <div className="flex items-center justify-center gap-3 py-2">
              <button
                onClick={() => onToggleMerge(device.baseSN!)}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  isMergedDevice 
                    ? 'bg-cyan-600 text-white' 
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                單一方塊
              </button>
              <button
                onClick={() => onToggleMerge(device.baseSN!)}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  !isMergedDevice 
                    ? 'bg-cyan-600 text-white' 
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                個別方塊
              </button>
            </div>
          )}

          {/* Location and SN */}
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{device.location || device.groupName || ''}</span>
            <div className="flex items-center gap-1">
              <span className="font-mono">{displaySN}</span>
              <button onClick={handleCopySN} className="text-slate-400 hover:text-white">
                <Copy className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Template01_Modern;
